import { defineStore } from 'pinia'
import { useAuthStore } from '~/stores/auth'

export const useNotificationStore = defineStore('notifications', {
    state: () => ({
        lastSyncDates: {
            users: null,
            roles: null,
            contactRequests: null
        },

        notificationCounts: {
            users: { new: { count: 0 }, updated: { count: 0 }, total: 0 },
            roles: { new: { count: 0 }, updated: { count: 0 }, total: 0 },
            contactRequests: { new: { count: 0 }, updated: { count: 0 }, total: 0 }
        },

        isLoading: false,
        lastCheck: null,
        refreshTimer: null
    }),

    getters: {
        hasNotifications: (state) => {
            return Object.values(state.notificationCounts).some(entity => entity.total > 0)
        },

        totalNotifications: (state) => {
            return Object.values(state.notificationCounts)
                .reduce((sum, entity) => sum + entity.total, 0)
        },

        notificationsByEntity: (state) => {
            return Object.entries(state.notificationCounts)
                .filter(([_, data]) => data.total > 0)
                .map(([entity, data]) => ({
                    entity,
                    ...data,
                    displayName: state.getEntityDisplayName(entity)
                }))
                .sort((a, b) => b.total - a.total)
        },

        getEntityDisplayName: (state) => (entityName) => {
            const names = {
                users: 'Utilisateurs',
                roles: 'Rôles',
                contactRequests: 'Demandes de contact'
            }
            return names[entityName] || entityName
        }
    },

    actions: {
        async checkForUpdates() {
            if (this.isLoading) return

            const authStore = useAuthStore()
            if (!authStore.isAuthenticated) return

            try {
                this.isLoading = true

                const params = {}
                if (this.lastSyncDates.users) params.users = this.lastSyncDates.users
                if (this.lastSyncDates.roles) params.roles = this.lastSyncDates.roles
                if (this.lastSyncDates.contactRequests) params.contactRequests = this.lastSyncDates.contactRequests

                const { getNotificationCounts } = useApi()
                const response = await getNotificationCounts(params)

                if (response.status === 'success') {
                    this.notificationCounts = response.data
                    this.lastCheck = new Date().toISOString()
                }

            } catch (error) {
                console.error('Erreur lors de la vérification des notifications:', error)
            } finally {
                this.isLoading = false
            }
        },

        async getNotificationDetails(entityName = null) {
            try {
                this.isLoading = true

                const params = {}
                if (this.lastSyncDates.users) params.users = this.lastSyncDates.users
                if (this.lastSyncDates.roles) params.roles = this.lastSyncDates.roles
                if (this.lastSyncDates.contactRequests) params.contactRequests = this.lastSyncDates.contactRequests

                const { getNotifications } = useApi()
                const response = await getNotifications(params)

                if (response.status === 'success') {
                    return entityName ? response.data[entityName] : response.data
                }

                return null

            } catch (error) {
                console.error('Erreur lors de la récupération des détails:', error)
                return null
            } finally {
                this.isLoading = false
            }
        },

        async markEntityAsSynced(entityName) {
            try {
                const newSyncDate = new Date().toISOString()

                // Marquer côté serveur
                const { markEntityAsSynced } = useApi()
                await markEntityAsSynced({
                    entity: entityName,
                    lastSync: newSyncDate
                })

                // Mettre à jour localement
                this.lastSyncDates[entityName] = newSyncDate



                // Remettre à zéro les compteurs pour cette entité
                this.notificationCounts[entityName] = {
                    new: { count: 0 },
                    updated: { count: 0 },
                    total: 0
                }

            } catch (error) {
                console.error(`Erreur lors du marquage de ${entityName}:`, error)
            }
        },

        async markAllAsSynced() {
            const entities = Object.keys(this.lastSyncDates)
            await Promise.all(entities.map(entity => this.markEntityAsSynced(entity)))
        },

        resetNotifications() {
            Object.keys(this.lastSyncDates).forEach(entity => {
                this.lastSyncDates[entity] = null

            })

            Object.keys(this.notificationCounts).forEach(entity => {
                this.notificationCounts[entity] = {
                    new: { count: 0 },
                    updated: { count: 0 },
                    total: 0
                }
            })
        },

        startPeriodicCheck(intervalMs = 30000) {
            // Arrêter le timer existant s'il existe
            this.stopPeriodicCheck()

            // Vérification initiale
            this.checkForUpdates()

            // Vérifications périodiques
            this.refreshTimer = setInterval(() => {
                const authStore = useAuthStore()
                if (authStore.isAuthenticated) {
                    this.checkForUpdates()
                }
            }, intervalMs)

            return this.refreshTimer
        },

        stopPeriodicCheck() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer)
                this.refreshTimer = null
            }
        }
    },

    persist: {
        key: 'notifications',
        secure: process.env.NODE_ENV === 'production',
        paths: ['lastSyncDates', 'lastCheck']
    }
})